/*
  Sticky buy bar.

  Three jobs:
  1. Reveal the bar once the real Add to Cart button has scrolled out of view, using an
     IntersectionObserver on Dawn's own buy-buttons block rather than a scroll listener.
  2. Keep the hidden variant id, the price and the pack label in step with Dawn's
     variant-change event.
  3. Add to the cart WITHOUT leaving the page, through the same route and the same cart
     rendering Dawn's product-form uses.

  All price strings are pre-rendered per variant in Liquid, so no money is formatted here.
  The bar ships hidden and stays hidden if anything is missing — it must never cover the
  page with a stale price.
*/
if (!customElements.get('shintop-sticky-bar')) {
  customElements.define(
    'shintop-sticky-bar',
    class ShintopStickyBar extends HTMLElement {
      connectedCallback() {
        this.idInput = this.querySelector('[data-shintop-sticky-id]');
        this.qtyInput = this.querySelector('[data-shintop-sticky-qty]');
        this.priceEl = this.querySelector('[data-shintop-sticky-price]');
        this.packEl = this.querySelector('[data-shintop-sticky-pack]');
        this.dealEl = this.querySelector('[data-shintop-sticky-deal]');
        this.addButton = this.querySelector('[data-shintop-sticky-add]');
        this.labelEl = this.querySelector('[data-shintop-sticky-label]');
        this.errorEl = this.querySelector('[data-shintop-sticky-error]');
        this.form = this.querySelector('form');
        this.data = this.readData();

        this.form?.addEventListener('submit', (event) => this.onSubmit(event));
        this.watchBuyButtons();
        this.watchDrawers();
        this.watchHeight();
        this.watchQuantity();
        this.showDeal();
        this.subscribeToVariant();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.drawerObserver?.disconnect();
        this.heightObserver?.disconnect();
        this.unsubscribe?.();
        this.unsubscribeQty?.();
        if (this.syncOnShow) window.removeEventListener('pageshow', this.syncOnShow);
        document.documentElement.classList.remove('shintop-sticky-bar-shown');
      }

      /*
        THE ONE PLACE THE BAR SHOWS OR HIDES. The class on <html> is what
        component-shintop-sticky-bar.css keys the focus clearance and the footer padding off; it
        replaced `html:has(shintop-sticky-bar:not([hidden]))`, which made every style recalc on
        the page six to seven times dearer (audit 2026-10-14, S05-001). Written here and only
        here, beside `hidden` — never set `this.hidden` or the class anywhere else.

        TWO CONDITIONS, NOT ONE (audit 2026-10-14, S06-004, P2): the real button has been scrolled
        past, AND no drawer or modal owns the screen. The bar is `position: fixed; z-index: 4` in
        the root stacking context and Dawn's sticky header, which holds the mobile menu drawer and
        the search modal, is `z-index: 3` — so below 990px the open menu's last items (Log in,
        Instagram) sat under the bar and hit-tested to its price and its Add to cart, which stayed
        live over the drawer. "A drawer owns the screen" is the gallery's test, unchanged
        (shintop-gallery.js, `modalOwnsTheScreen`), so the two components share one definition.

        AND IT GIVES FOCUS BACK. Hiding the bar under the CART drawer would have broken something
        Dawn already did right: the drawer opened from the bar's own Add returns focus to that
        button on close, but cart-drawer.js calls `removeTrapFocus(this.activeElement)` BEFORE it
        takes `overflow-hidden` off <body> — so the button is still `display: none` when Dawn
        focuses it, the call does nothing, and a keyboard shopper lands on <body>. So when a drawer
        hides the bar while focus is inside it, the bar keeps that element and focuses it the
        moment the drawer lets go: it finishes Dawn's own return and never picks a new target. A
        drawer that closes while the button is still scrolled out of view drops the debt.

        THE CLASS FOLLOWS THE SCROLL, NOT THE DRAWER — so under a drawer the two disagree, on
        purpose. The class reserves the bar's strip at the end of the page (the footer's bottom
        padding, and the focus clearance), and taking it off under a drawer moved the page:
        measured on dev at the very bottom, the page clamped 69px up behind the cart drawer at
        1440 (73 at 390), and after Escape the footer's last line sat at 873 under a bar starting
        at 827 — UIQ-003 back. The page is locked while a drawer is open, so the strip costs
        nothing there; it simply stays for the bar that is about to come back.
      */
      update() {
        const drawer = this.drawerOwnsTheScreen();
        const showing = Boolean(this.scrolledPast) && !drawer;
        if (drawer && !this.hidden && this.contains(document.activeElement)) this.focusOwed = document.activeElement;
        this.hidden = !showing;
        document.documentElement.classList.toggle('shintop-sticky-bar-shown', Boolean(this.scrolledPast));
        if (drawer) return;
        const owed = this.focusOwed;
        this.focusOwed = null;
        if (showing && owed && this.contains(owed)) owed.focus({ preventScroll: true });
      }

      /*
        Dawn's cart drawer, menu drawer and modal dialogs all put `overflow-hidden` (or
        `overflow-hidden-<breakpoint>`) on <body> while open, and nothing else on this theme does.
      */
      drawerOwnsTheScreen() {
        return Array.prototype.some.call(document.body.classList, (name) => name.startsWith('overflow-hidden'));
      }

      /*
        One attribute on one element, so the cost is a callback per drawer opening or closing —
        not a root `:has()`, which is what S05-001 took out of this component's stylesheet.
      */
      watchDrawers() {
        if (!('MutationObserver' in window)) return;
        this.drawerObserver = new MutationObserver(() => this.update());
        this.drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      }

      /*
        PUBLISH THE BAR'S OWN HEIGHT so the page can keep a focused control clear of it — WCAG
        2.2 SC 2.4.11 Focus Not Obscured (audit 2026-09-13, S06-02, P1). The consumer is one
        declaration in component-shintop-sticky-bar.css, which reads this as
        --shintop-sticky-bar-h and turns it into `scroll-padding-bottom` on the scroller; the
        reason it lives here rather than as a constant there is that the bar's height is not one
        number. The deal strip and the error strip each add a line, and the error one appears
        while the bar is already on screen.

        A ResizeObserver for exactly that reason — a single read at reveal time would be correct
        until the first failed add. It fires with 0 while the bar is `display: none`, and that
        zero is dropped rather than published: a reserved strip of 0 is the unfixed page, and the
        last good height is the right thing to keep, since nothing reads it while the bar is
        hidden (under a drawer the class stays on, and the strip it reserves keeps that last good
        height — `update()` says why).

        Written on <html> because that is the element the rule matches; a custom property set on
        the bar itself would inherit downward and never reach the scroller.
      */
      watchHeight() {
        if (!('ResizeObserver' in window)) return;
        this.heightObserver = new ResizeObserver(() => {
          const height = this.offsetHeight;
          if (height) document.documentElement.style.setProperty('--shintop-sticky-bar-h', `${height}px`);
        });
        this.heightObserver.observe(this);
      }

      /*
        The bar adds what the buy box's quantity box says, not a hardcoded 1 — setting 3
        and tapping the bar used to add one box (Naoya, round 5). The button then names
        the number, because a control that silently adds three is worse than one that
        adds one: "Add 3 to cart".
      */
      watchQuantity() {
        this.buyBoxQty = document.querySelector('product-info input[name="quantity"]');
        if (!this.buyBoxQty || !this.qtyInput) return;

        this.syncQuantity();
        const sync = () => this.syncQuantity();
        this.buyBoxQty.addEventListener('input', sync);
        this.buyBoxQty.addEventListener('change', sync);

        /*
          DAWN CHANGES THAT INPUT WITHOUT A DOM EVENT (audit 2026-09-13, S03-01, P1).
          `ProductInfo.setQuantityBoundries()` ends with `this.quantityInput.value = min` and
          then publishes `quantityUpdate` and nothing else (assets/product-info.js:349-351) —
          no `input`, no `change`, so the two listeners above never hear it. And it runs on
          EVERY `cartUpdate`, including the bar's own (:44 -> fetchQuantityRules ->
          updateQuantityRules -> :371). Measured live: the shopper sets 3, adds 3, Dawn resets
          the visible box to 1, and the bar still reads "Add 3 to cart" — one tap adds three
          more. That is the exact defect this bar was built to fix, coming back through a
          channel it was not listening on.

          The element is updated in place rather than replaced, so `this.buyBoxQty` stays
          valid; it is stale, not detached, which is why re-reading it is the whole fix.
        */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.quantityUpdate) {
          this.unsubscribeQty = subscribe(PUB_SUB_EVENTS.quantityUpdate, sync);
        }

        /*
          THE BROWSER RESTORES THAT INPUT WITHOUT AN EVENT TOO (audit 2026-10-14, H08-3). On a Back
          that the back/forward cache does not serve, Chrome refills the buy box's quantity from
          its form state AFTER DOMContentLoaded — measured on dev, 1440, Chrome 155 with
          `--disable-features=BackForwardCache`: the box read 1 at DOMContentLoaded and 3 at `load`
          and `pageshow`, while the bar, synced at connect, still read "Add to cart" with 1 in its
          field. A tap added one box under a box that said three. `pageshow` fires after that
          restore on every show, cached or not, so one more read closes it. Safari unmeasured.
        */
        this.syncOnShow = sync;
        window.addEventListener('pageshow', sync);
      }

      syncQuantity() {
        const wanted = Math.max(parseInt(this.buyBoxQty.value, 10) || 1, 1);
        this.qtyInput.value = wanted;

        // Never overwrite the gated or sold-out wording — those are not about quantity.
        if (!this.labelEl || this.addButton.disabled) return;
        this.labelEl.textContent = this.addLabel();
      }

      addLabel() {
        const wanted = parseInt(this.qtyInput?.value, 10) || 1;
        const many = this.dataset.strAddCount;
        return wanted > 1 && many ? many.replace('[count]', wanted) : this.dataset.strAdd;
      }

      /*
        The deal line is MIRRORED from the buy box's qty-deal element — the existence of
        that element IS the deal's gate, so the bar needs no copy of it, and the sentence
        is already server-rendered from price × metafield.

        It mirrors the STATE-FREE sentence, and both halves of that are deliberate. Two
        earlier builds of this line were wrong in ways only looking and reading caught: the
        buy box's worked sentence beside the bar's own price left a cold-reader unable to
        tell which number they would be charged, and the card's "in your cart" wording is
        false on a PDP, where the quantity box has not written to the cart yet. One always
        true sentence has no such states — and nothing to watch, so the bar's height cannot
        move when the threshold is crossed.
      */
      showDeal() {
        const deal = document.querySelector('shintop-qty-deal');
        if (!deal || !this.dealEl) return;
        this.dealEl.textContent = deal.dataset.strBar || '';
      }

      /*
        The bar's own form used to POST full-page, so tapping Add — the one control the
        bar exists to provide — threw the shopper onto /cart and out of the page they
        were reading. The add now goes the way Dawn's product-form goes it: same
        cart_add_url, the cart's sections bundled into the same request — and then the
        shared after-write step every Shintop writer takes (assets/shintop-cart.js), which
        renders the drawer, repaints the bubble, opens the drawer with this bar's control as
        its opener and publishes the whole cart (audit 2026-10-14, R04-1, chunk 8).

        ⚠️ THEY DO NOT BEHAVE IDENTICALLY, AND THIS COMMENT USED TO SAY THEY DID (S08-04,
        audit 2026-09-13, chunk 9). The add PATH is the same; the add is not. Three
        differences, all of them real in the code today:

          1. The add-on never fires from this bar. `shintop-addon.js` filters on
             `source === 'product-form'`, so a shopper who ticked the add-on in the buy box
             and then added from the bar gets the product without it. Unreachable while no
             add-on is armed.
          2. The bar's form carries no line-item properties. Anything the buy box collects
             as a property is absent from a bar add. Unreachable while no product defines one.
          3. The quantity can differ. Fixed in chunk 1 (S03-01) by mirroring the buy box's
             quantity into the bar; before that the bar always submitted 1.

        Only (3) could fire when the audit ran. (1) and (2) are one admin decision away
        each, so if you arm an add-on or add a line-item property, come back here first.

        The plain POST is still the floor. If the cart element, fetchConfig or routes are
        missing (JS off, an older Dawn, a stripped page) the submit is left alone and the
        browser posts the form — a worse add, but an add.
      */
      onSubmit(event) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (!cart || typeof fetchConfig !== 'function' || typeof routes === 'undefined') return;

        event.preventDefault();
        if (this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true') return;
        this.addButton.setAttribute('aria-disabled', 'true');
        this.showError('');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const helper = window.shintopCart;
        formData.append('sections', helper ? helper.sections() : cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        const opener = document.activeElement;
        cart.setActiveElement(opener);
        config.body = formData;

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((parsed) => {
            // A 422 (inventory, or a line cap already reached) arrives as 200 with a
            // status field. Say so in the bar rather than opening an empty notification.
            if (parsed.status) {
              this.showError(parsed.description || parsed.message);
              return;
            }
            // `productVariantId` rides along as Dawn's product form sends it; the publish itself
            // (whole cart, for the card steppers and everything else listening) is the helper's.
            if (helper) {
              helper.afterCartWrite(parsed, {
                source: 'shintop-sticky-bar',
                productVariantId: formData.get('id'),
                open: true,
                opener,
              });
              return;
            }
            // The helper failed to load: the path as it was before the helper existed.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'shintop-sticky-bar',
                productVariantId: formData.get('id'),
                cartData: parsed,
              });
            }
            cart.renderContents(parsed);
            cart.classList.remove('is-empty');
          })
          .catch(() => this.showError(this.dataset.strError))
          .finally(() => this.addButton.removeAttribute('aria-disabled'));
      }

      showError(message) {
        if (this.errorEl) this.errorEl.textContent = message || '';
      }

      readData() {
        const node = this.querySelector('[data-shintop-sticky-data]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch {
          return null;
        }
      }

      watchBuyButtons() {
        // Dawn's real add-to-cart lives in .product-form__buttons inside product-info.
        const anchor = document.querySelector('product-info .product-form__buttons');
        if (!anchor || !('IntersectionObserver' in window)) return;

        // The bar appears once the real button has been scrolled PAST, not merely
        // whenever it is off-screen (2026-08-31 audit, F7). "Not intersecting" is also
        // true at the TOP of the page, where the button is below the fold and the
        // shopper has not yet seen the price — on mobile that showed a labelled price
        // bar over ~11% of the first viewport before the buy box ever appeared. Desktop
        // already behaved (the buy box is in the first viewport there), and keeps
        // behaving; this makes mobile match the header comment's own contract: "once
        // the real button has scrolled out of view".
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.update();
          },
          { rootMargin: '0px' },
        );
        this.observer.observe(anchor);
      }

      subscribeToVariant() {
        if (typeof subscribe !== 'function' || !this.data) return;

        const eventName =
          typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange
            ? PUB_SUB_EVENTS.variantChange
            : 'variant-change';

        this.unsubscribe = subscribe(eventName, ({ data }) => {
          if (!data) return;
          this.render(data.variant);
        });
      }

      render(variant) {
        // No variant means the current option combination is unavailable. Disable rather
        // than hide, so the bar does not jump around under the shopper's thumb.
        if (!variant) {
          if (this.addButton) this.addButton.disabled = true;
          return;
        }

        /*
          S03-13 (audit 2026-09-13, chunk 9). A VARIANT MISSING FROM THE BAKED MAP MUST NOT
          KEEP THE PREVIOUS VARIANT'S VALUES. `this.data` is rendered in Liquid at page
          build; a variant that arrives without a row in it used to hit a bare
          `if (!entry) return`, leaving the bar showing the last variant's price and pack
          label — and, far worse, leaving `input[name="id"]` holding the LAST variant's id,
          which is what a tap on Add submits. The shopper is looking at one thing and buying
          another, with nothing on screen to say so.

          So the id is written first, from `variant.id`, which is always known. Then, with no
          row to render from, the bar says nothing rather than something false and the Add
          button is disabled — the same choice the `!variant` branch above makes, and for the
          same reason.

          Latent: 0 of 89 products were multi-variant when this was written (re-counted in the
          audit). It opens on the first multi-variant product, where nothing would warn anyone.
        */
        const entry = this.data[variant.id];
        if (this.idInput) this.idInput.value = variant.id;

        if (!entry) {
          if (this.priceEl) this.priceEl.textContent = '';
          if (this.packEl) this.packEl.textContent = '';
          if (this.addButton) this.addButton.disabled = true;
          return;
        }

        if (this.priceEl) this.priceEl.textContent = entry.price;
        if (this.packEl) this.packEl.textContent = entry.title === 'Default Title' ? '' : entry.title;
        if (this.addButton) this.addButton.disabled = !entry.available;

        /*
          THE LABEL COMES FROM THE SAME FACT AS THE BUTTON (audit 2026-10-14, S03-017). Only the
          button used to follow `entry.available`; the label kept whatever the page was built
          with, and `syncQuantity()` will not touch a disabled button's wording — so a switch to a
          sold-out pack read "Add to cart" on a dead button, and a switch back from one read "Sold
          out" on a live one (step 08's harness, both directions). The unavailable wording is the
          server's own string (`data-str-sold-out`, which already says "Temporarily out" on a
          gated kit); nothing here formats money. Latent until the first multi-variant product.
        */
        if (this.labelEl) this.labelEl.textContent = entry.available ? this.addLabel() : this.dataset.strSoldOut;
      }
    },
  );
}
